/**
 * The routes `/api/v1` actually has, as the SPA would use them.
 *
 * Checked against `docs/api/endpoint-permissions.md` — which is generated from the running route
 * table — by `routes.test.ts`. That gate exists because the previous version of this client
 * invented routes: `/bills/submit`, `/accounts`, `/settings/numbering`, a dozen more. An invented
 * route is worse than a missing one, because it looks implemented and fails as a 404 somewhere far
 * from the mistake.
 */
export const API_ROUTES = {
  session: "/session",
  subscriptionStatus: "/subscription-status",
  subscriptionExport: "/subscription/export",
  checkout: "/subscription/checkout",
  payBill: "/subscription/bills/pay",
  checkoutQuote: "/subscription/checkout/quote",
  checkoutConfirm: "/subscription/checkout/{id}/confirm",
  checkoutReturn: "/subscription/checkout/return",
  subscriptionAccount: "/subscription/account",
  subscriptionChanges: "/subscription/changes",
  subscriptionChangeUndo: "/subscription/changes/{id}/undo",
  subscriptionBilling: "/subscription/billing",

  parties: "/parties",
  party: "/parties/{id}",
  partyStanding: "/parties/{id}/standing",
  partyStatement: "/parties/{id}/statement",
  billingItems: "/billing-items",
  invoiceSetting: "/settings/invoicing",
  additionalCharges: "/settings/invoicing/charges",
  additionalCharge: "/settings/invoicing/charges/{id}",
  paymentOptions: "/settings/invoicing/payment-options",
  paymentOption: "/settings/invoicing/payment-options/{id}",
  billingItem: "/billing-items/{id}",
  partyReconciliations: "/parties/{id}/reconciliations",

  invoices: "/invoices",
  invoice: "/invoices/{id}",
  invoiceHeader: "/invoices/{id}/header",
  invoiceIssue: "/invoices/{id}/issue",
  invoicePosting: "/invoices/{id}/posting",
  invoiceIssued: "/invoices/{id}/issued",
  invoiceBis: "/invoices/{id}/bis",
  invoiceOutstanding: "/invoices/{id}/outstanding",
  invoiceCredits: "/invoices/{id}/credits",

  creditNotes: "/credit-notes",
  creditNote: "/credit-notes/{id}",
  creditNoteHeader: "/credit-notes/{id}/header",
  creditNoteIssue: "/credit-notes/{id}/issue",
  creditNotePosting: "/credit-notes/{id}/posting",
  creditNoteIssued: "/credit-notes/{id}/issued",

  receipts: "/receipts",
  receipt: "/receipts/{id}",
  receiptApplications: "/receipts/{id}/applications",
  receiptPosting: "/receipts/{id}/posting",

  bills: "/bills",
  billFigures: "/bills/figures",
  bill: "/bills/{id}",
  billHeader: "/bills/{id}/header",
  billApproval: "/bills/{id}/approval",
  billCredits: "/bills/{id}/credits",

  quotations: "/quotations",
  quotation: "/quotations/{id}",
  quotationNumber: "/quotations/{id}/number",
  quotationSending: "/quotations/{id}/sending",
  quotationPdf: "/quotations/{id}/pdf",
  quotationEmail: "/quotations/{id}/email",
  quotationEmails: "/quotations/{id}/emails",
  quotationResponse: "/quotations/{id}/response",
  quotationDuplicate: "/quotations/{id}/duplicate",
  quotationConversion: "/quotations/{id}/conversion",
  purchaseOrders: "/purchase-orders",
  purchaseOrder: "/purchase-orders/{id}",
  purchaseOrderNumber: "/purchase-orders/{id}/number",
  purchaseOrderSending: "/purchase-orders/{id}/sending",
  purchaseOrderConfirmation: "/purchase-orders/{id}/confirmation",
  purchaseOrderCancellation: "/purchase-orders/{id}/cancellation",
  purchaseOrderReceipts: "/purchase-orders/{id}/receipts",
  purchaseOrderConversion: "/purchase-orders/{id}/conversion",
  purchaseOrderPdf: "/purchase-orders/{id}/pdf",
  purchaseOrderEmail: "/purchase-orders/{id}/email",
  purchaseOrderEmails: "/purchase-orders/{id}/emails",
  submitPurchaseOrder: "/approvals/documents/purchase_order/{id}/submission",
  payments: "/payments",
  payment: "/payments/{id}",
  paymentPosting: "/payments/{id}/posting",

  debitNotes: "/debit-notes",
  debitNote: "/debit-notes/{id}",
  debitNoteHeader: "/debit-notes/{id}/header",
  debitNoteApproval: "/debit-notes/{id}/approval",

  bankAccounts: "/banking/accounts",
  bankAccount: "/banking/accounts/{id}",
  bankStatement: "/banking/accounts/{id}/statement",
  bankStatementOfx: "/banking/accounts/{id}/statement/ofx",
  bankReconciliation: "/banking/accounts/{id}/reconciliation",
  bankMatches: "/banking/accounts/{id}/matches",
  statementMatchCandidates: "/banking/statement-lines/{id}/candidates",
  statementMatch: "/banking/statement-lines/{id}/match",
  statementMatches: "/banking/statement-lines/{id}/matches",
  statementEntry: "/banking/statement-lines/{id}/entry",
  reconciliationLocks: "/banking/accounts/{id}/reconciliation-locks",
  reconciliationReopen: "/banking/reconciliation-locks/{id}/reopen",
  statementUnmatch: "/banking/matches/{id}",
  transfers: "/banking/transfers",
  transfer: "/banking/transfers/{id}",
  transferPosting: "/banking/transfers/{id}/posting",

  journals: "/accounting/journals",
  journal: "/accounting/journals/{id}",
  journalPosting: "/accounting/journals/{id}/posting",
  journalDiscard: "/accounting/journals/{id}/discard",

  setup: "/settings/setup",
  users: "/settings/users",
  userInvitation: "/settings/users/{id}/invitation",
  audit: "/settings/audit",
  overviewFigures: "/reports/overview",
  incomeStatement: "/reports/income-statement",
  chartAccounts: "/accounting/chart/accounts",
  chartAccountName: "/accounting/chart/accounts/{id}/name",
  fiscalYears: "/accounting/fiscal-years",
  numbering: "/settings/numbering",
  taxCodes: "/settings/tax-codes",
  withholdingCodes: "/settings/withholding-codes",
  countryPack: "/settings/localization",
  emailTemplates: "/settings/email-templates",
  emailTemplate: "/settings/email-templates/{id}",
  invoicePdf: "/invoices/{id}/pdf",
  invoiceEmail: "/invoices/{id}/email",
  invoiceEmails: "/invoices/{id}/emails",
  invoiceReminder: "/invoices/{id}/reminder",
  statementEmail: "/parties/{id}/statement/email",
  partyEmails: "/parties/{id}/emails",
  creditNotePdf: "/credit-notes/{id}/pdf",
  receiptPdf: "/receipts/{id}/pdf",
  documentPdf: "/documents/pdf/{id}",
  branding: "/settings/branding",
  brandingLogo: "/settings/branding/logo",
  taxNames: "/settings/tax-names",
  taxCodeName: "/settings/tax-codes/{id}/name",
  withholdingCodeName: "/settings/withholding-codes/{code}/name",
  chart: "/accounting/chart",
  chartAccount: "/accounting/chart/accounts/{id}",
  chartAccountUsage: "/accounting/chart/accounts/{id}/usage",
  chartAccountActive: "/accounting/chart/accounts/{id}/active",
  chartTemplates: "/accounting/chart/templates",
  chartTemplateDetail: "/accounting/chart/templates/{code}/{version}",
  chartTemplatePreview: "/accounting/chart/template/preview",
  chartTemplate: "/accounting/chart/template",
  chartRoles: "/accounting/chart/roles",

  periodClose: "/accounting/periods/{id}/close",
  periodReopen: "/accounting/periods/{id}/reopen",
  periodLockDate: "/accounting/periods/{id}/lock-date",
  periodHistory: "/accounting/periods/{id}/history",

  trialBalance: "/reports/trial-balance",
  trialBalanceAsOn: "/reports/trial-balance/as-on",
  generalLedger: "/reports/general-ledger",
  ledgerDocuments: "/reports/general-ledger/documents",
  cashFlow: "/reports/cash-flow",
  vatReturn: "/reports/vat-return",
  withheldByCustomers: "/reports/withheld-by-customers",
  reportExport: "/reports/export",
  ageing: "/reports/ageing",
  receivableAnalysis: "/reports/receivable-analysis",
  payableAnalysis: "/reports/payable-analysis",
  openItems: "/reports/open-items",
  withholdingRegister: "/reports/withholding-register",

  yearEnd: "/accounting/fiscal-years/{id}/year-end",
  yearEndManual: "/accounting/fiscal-years/{id}/year-end/manual",
  yearReopen: "/accounting/fiscal-years/{id}/reopen",
  setupSteps: "/settings/setup/steps",
  setupStep: "/settings/setup/steps/{step}",
  reportFrameworks: "/accounting/reporting/frameworks",
  reportTags: "/accounting/reporting/frameworks/{framework}/tags",
  reportTag: "/accounting/reporting/frameworks/{framework}/tags/{id}",
  reportTagArchive: "/accounting/reporting/frameworks/{framework}/tags/{id}/archive",
  accountMapping: "/accounting/reporting/mapping/{framework}",
  accountMappingHistory: "/accounting/reporting/mapping/{framework}/{accountId}/history",
  reportTemplates: "/accounting/reporting/templates",
  reportTemplate: "/accounting/reporting/templates/{code}",
  reportTemplateById: "/accounting/reporting/templates/{id}",
  reportTemplateClone: "/accounting/reporting/templates/{code}/clone",
  reportTemplatePreview: "/accounting/reporting/templates/preview",
  statement: "/accounting/reporting/statements",
  statementFinalization: "/accounting/reporting/statements/finalization",
  statementSnapshot: "/accounting/reporting/snapshots/{id}",
  employees: "/accounting/employees",
  employeeMovements: "/accounting/employees/movements",
  employeeMovementDrafts: "/accounting/employees/movements/drafts",
  employeeMovement: "/accounting/employees/movements/{id}",
  employeeMovementPosting: "/accounting/employees/movements/{id}/posting",
  submitYearClose: "/approvals/documents/year_close/{id}/submission",
  yearCloseGates: "/accounting/fiscal-years/{id}/gates",
  yearSoftLock: "/accounting/fiscal-years/{id}/soft-lock",
  yearCloseWithdrawal: "/accounting/fiscal-years/{id}/approval/withdrawal",
  yearWorkpaper: "/accounting/fiscal-years/{id}/workpaper",
  yearWorkpaperExport: "/accounting/fiscal-years/{id}/workpaper/export",
  yearEndMethod: "/accounting/year-end-method",
  yearCloseChecks: "/accounting/fiscal-years/{id}/close-checks",
  yearCloseItems: "/accounting/fiscal-years/{id}/close-checks/{workstream}/items",
  yearCloseDone: "/accounting/fiscal-years/{id}/close-checks/{workstream}/done",
  yearCloseNotApplicable: "/accounting/fiscal-years/{id}/close-checks/{workstream}/not-applicable",
  yearCloseReopen: "/accounting/fiscal-years/{id}/close-checks/{workstream}/reopen",
  nextFiscalYear: "/accounting/fiscal-years/{id}/next",
  company: "/settings/company",

  approvalPolicy: "/approvals/policy",
  approvalPolicyHistory: "/approvals/policy/history",
  approvalPolicyFor: "/approvals/policy/{documentType}",
  approvalRules: "/approvals/rules",
  approvalQueue: "/approvals/queue",
  approvalDocument: "/approvals/documents/{documentType}/{id}",
  submitInvoice: "/approvals/documents/sales_invoice/{id}/submission",
  submitCreditNote: "/approvals/documents/credit_note/{id}/submission",
  submitBill: "/approvals/documents/supplier_bill/{id}/submission",
  submitDebitNote: "/approvals/documents/supplier_debit_note/{id}/submission",
  submitFundTransfer: "/approvals/documents/fund_transfer/{id}/submission",
  submitJournal: "/approvals/documents/manual_journal/{id}/submission",
  submitPayment: "/approvals/documents/supplier_payment/{id}/submission",
  approvalVerification: "/approvals/requests/{id}/verification",
  approvalApproval: "/approvals/requests/{id}/approval",
  approvalReturn: "/approvals/requests/{id}/return",

  inboundPreview: "/inbound-documents/preview",

  // Help (ADR-0010): a pass-through to the platform support module.
  helpStatus: "/support/help-status",
  helpHome: "/support/kb/home",
  helpNav: "/support/kb/nav",
  helpCategory: "/support/kb/categories/{slug}",
  helpArticle: "/support/kb/articles/{slug}",
  helpSearch: "/support/kb/search",
  helpReadEvents: "/support/kb/read-events",
  helpReadBeacon: "/support/kb/read-events/{id}/beacon",
  supportCategories: "/support/categories",
  supportTickets: "/support/tickets",
  supportTicket: "/support/tickets/{id}",
  supportTicketMessages: "/support/tickets/{id}/messages",
  supportUploads: "/support/uploads",
  supportAttachment: "/support/attachments/{id}/download",
} as const;

/** Where each kind of email's draft and send live. */
export const EMAIL_ROUTES = {
  invoice: API_ROUTES.invoiceEmail,
  quotation: API_ROUTES.quotationEmail,
  purchaseOrder: API_ROUTES.purchaseOrderEmail,
  reminder: API_ROUTES.invoiceReminder,
  statement: API_ROUTES.statementEmail,
} as const;

export type ApiRoute = (typeof API_ROUTES)[keyof typeof API_ROUTES];

/**
 * Routes `/api/v1` publishes that this client deliberately does not name, and why.
 *
 * The list exists so the check can run **both ways**. Naming a route the API does not have was the
 * original defect and is caught above; the opposite went unnoticed for a whole pass — twelve
 * endpoints were added to the API in one sitting and nothing here said the SPA had fallen behind.
 * A member that silently never appears is harder to find than one that 404s.
 *
 * Adding a route to this list is the deliberate act of saying "the SPA is not the client for
 * this". Leaving one out is what the test is for.
 */
export const NOT_FOR_THE_SPA: Readonly<Record<string, string>> = {
  "/api/v1/inbound-documents/preview":
    "Rigo Billing's intake, not a screen. The SPA reads what the sync produced, never the payload.",
  "/api/v1/invoices/{id}/bis":
    "The EN 16931 payload, for the network and the IRD. A person downloads it; the SPA does not parse it.",
  "/api/v1/credit-notes/{id}/bis":
    "As the invoice's — the structured payload, not a screen's data.",
};

/**
 * Every `RigoApi` member that this client cannot serve, and why — the two reasons being different
 * jobs for whoever finishes the wiring (build plan 2.6, review R6).
 *
 * `"no route"` means `/api/v1` has nothing yet. `"/some/route"` means the route exists and the
 * SPA's shape has not been reconciled with the API's — `T.BankAccount` carries a `balance` and a
 * `kind` that `GET /banking/accounts` deliberately does not return, because a bank account holds no
 * balance (`01-schema.md` §1.2); `T.Session` carries a user, a permission list and a fiscal year
 * that `GET /session` does not; `T.Party` carries a running balance. None of those is a mapping
 * this client can invent, and inventing one is how a screen shows a number nobody computed.
 */
/**
 * The `RigoApi` members this client really implements, and the route each calls.
 *
 * The counterpart to `UNSERVED`, and the reason it is a separate map rather than an absence: the
 * gate below asks that *every* member be accounted for, so "wired" has to be sayable. Before this
 * existed the only accounted-for state was "waiting", and the first member anybody finished would
 * have failed the test that was meant to keep them honest.
 */
export const SERVED: Readonly<Record<string, ApiRoute>> = {
  "session.get": API_ROUTES.session,
  "subscription.status": API_ROUTES.subscriptionStatus,
  "subscription.exportData": API_ROUTES.subscriptionExport,
  "subscription.checkoutOffer": API_ROUTES.checkout,
  "subscription.payBill": API_ROUTES.payBill,
  "subscription.quoteCheckout": API_ROUTES.checkoutQuote,
  "subscription.placeOrder": API_ROUTES.checkout,
  "subscription.confirmPayment": API_ROUTES.checkoutConfirm,
  "subscription.confirmReturn": API_ROUTES.checkoutReturn,
  "subscription.account": API_ROUTES.subscriptionAccount,
  "subscription.scheduleChange": API_ROUTES.subscriptionChanges,
  "subscription.undoChange": API_ROUTES.subscriptionChangeUndo,
  "subscription.updateBilling": API_ROUTES.subscriptionBilling,
  "settings.roles": API_ROUTES.users,
  "settings.users": API_ROUTES.users,
  "settings.inviteUser": API_ROUTES.users,
  "settings.resendInvitation": API_ROUTES.userInvitation,
  "settings.audit": API_ROUTES.audit,
  "settings.approvalPolicy": API_ROUTES.approvalPolicy,
  "settings.setApprovalPolicy": API_ROUTES.approvalPolicyFor,
  "overview.get": API_ROUTES.overviewFigures,
  "approvals.queue": API_ROUTES.approvalQueue,
  "approvals.act": API_ROUTES.approvalApproval,
  "approvals.rules": API_ROUTES.approvalRules,
  "approvals.saveRules": API_ROUTES.approvalRules,
  "approvals.document": API_ROUTES.approvalDocument,
  "approvals.submit": API_ROUTES.submitInvoice,
  "reports.profitAndLoss": API_ROUTES.incomeStatement,
  "reports.generalLedger": API_ROUTES.generalLedger,
  "reports.ledgerDocuments": API_ROUTES.ledgerDocuments,
  "reports.trialBalanceAsOn": API_ROUTES.trialBalanceAsOn,
  "reports.cashFlow": API_ROUTES.cashFlow,
  "reports.ageing": API_ROUTES.ageing,
  "reports.receivableAnalysis": API_ROUTES.receivableAnalysis,
  "reports.payableAnalysis": API_ROUTES.payableAnalysis,
  "reports.openItems": API_ROUTES.openItems,
  "reports.vatReturn": API_ROUTES.vatReturn,
  "reports.withholding": API_ROUTES.withholdingRegister,
  "reports.exportToExcel": API_ROUTES.reportExport,
  "fundTransfers.list": API_ROUTES.transfers,
  "fundTransfers.get": API_ROUTES.transfer,
  "fundTransfers.create": API_ROUTES.transfers,
  "fundTransfers.post": API_ROUTES.transferPosting,
  "journals.list": API_ROUTES.journals,
  "journals.get": API_ROUTES.journal,
  "journals.saveDraft": API_ROUTES.journals,
  "journals.post": API_ROUTES.journalPosting,
  "journals.discard": API_ROUTES.journalDiscard,
  "bills.list": API_ROUTES.bills,
  "bills.correctable": API_ROUTES.bills,
  "bills.kpis": API_ROUTES.billFigures,
  "bills.get": API_ROUTES.bill,
  "bills.saveDraft": API_ROUTES.bills,
  "bills.discardDraft": API_ROUTES.bill,
  "bills.submit": API_ROUTES.bills,
  "bills.approve": API_ROUTES.billApproval,
  "bills.credits": API_ROUTES.billCredits,
  "bills.useCredit": API_ROUTES.billCredits,
  "debitNotes.list": API_ROUTES.debitNotes,
  "debitNotes.get": API_ROUTES.debitNote,
  "debitNotes.saveDraft": API_ROUTES.debitNotes,
  "debitNotes.discardDraft": API_ROUTES.debitNote,
  "debitNotes.approve": API_ROUTES.debitNoteApproval,
  "quotations.list": API_ROUTES.quotations,
  "quotations.get": API_ROUTES.quotation,
  "quotations.saveDraft": API_ROUTES.quotations,
  "quotations.discardDraft": API_ROUTES.quotation,
  "quotations.number": API_ROUTES.quotationNumber,
  "quotations.send": API_ROUTES.quotationSending,
  "quotations.respond": API_ROUTES.quotationResponse,
  "quotations.duplicate": API_ROUTES.quotationDuplicate,
  "quotations.convert": API_ROUTES.quotationConversion,
  "purchaseOrders.list": API_ROUTES.purchaseOrders,
  "purchaseOrders.get": API_ROUTES.purchaseOrder,
  "purchaseOrders.saveDraft": API_ROUTES.purchaseOrders,
  "purchaseOrders.discardDraft": API_ROUTES.purchaseOrder,
  "purchaseOrders.number": API_ROUTES.purchaseOrderNumber,
  "purchaseOrders.send": API_ROUTES.purchaseOrderSending,
  "purchaseOrders.confirm": API_ROUTES.purchaseOrderConfirmation,
  "purchaseOrders.cancel": API_ROUTES.purchaseOrderCancellation,
  "purchaseOrders.receive": API_ROUTES.purchaseOrderReceipts,
  "purchaseOrders.convert": API_ROUTES.purchaseOrderConversion,
  "payments.list": API_ROUTES.payments,
  "payments.get": API_ROUTES.payment,
  "payments.create": API_ROUTES.payments,
  "payments.post": API_ROUTES.paymentPosting,
  "payments.discard": API_ROUTES.payment,
  "payments.openBills": API_ROUTES.bills,
  "creditNotes.saveDraft": API_ROUTES.creditNotes,
  "creditNotes.discardDraft": API_ROUTES.creditNote,
  "creditNotes.issue": API_ROUTES.creditNoteIssue,
  "receipts.list": API_ROUTES.receipts,
  "receipts.get": API_ROUTES.receipt,
  "receipts.create": API_ROUTES.receipts,
  "receipts.openInvoicesFor": API_ROUTES.invoices,
  "invoices.credits": API_ROUTES.invoiceCredits,
  "invoices.useCredit": API_ROUTES.invoiceCredits,
  "invoices.list": API_ROUTES.invoices,
  "invoices.get": API_ROUTES.invoice,
  "invoices.saveDraft": API_ROUTES.invoices,
  "invoices.discardDraft": API_ROUTES.invoice,
  "invoices.issue": API_ROUTES.invoiceIssue,
  "creditNotes.list": API_ROUTES.creditNotes,
  "creditNotes.get": API_ROUTES.creditNote,
  "parties.search": API_ROUTES.parties,
  "parties.list": API_ROUTES.parties,
  "parties.get": API_ROUTES.party,
  "parties.details": API_ROUTES.party,
  "parties.create": API_ROUTES.parties,
  "parties.update": API_ROUTES.party,
  "parties.standing": API_ROUTES.partyStanding,
  "parties.statement": API_ROUTES.partyStatement,
  "banking.reconciliation": API_ROUTES.bankReconciliation,
  "banking.matches": API_ROUTES.bankMatches,
  "banking.candidates": API_ROUTES.statementMatchCandidates,
  "banking.match": API_ROUTES.statementMatch,
  "banking.matchSeveral": API_ROUTES.statementMatches,
  "banking.createEntry": API_ROUTES.statementEntry,
  "banking.locks": API_ROUTES.reconciliationLocks,
  "banking.lock": API_ROUTES.reconciliationLocks,
  "banking.reopen": API_ROUTES.reconciliationReopen,
  "banking.unmatch": API_ROUTES.statementUnmatch,
  "banking.importStatement": API_ROUTES.bankStatement,
  "settings.invoicing": API_ROUTES.invoiceSetting,
  "settings.saveInvoicing": API_ROUTES.invoiceSetting,
  "settings.additionalCharges": API_ROUTES.additionalCharges,
  "settings.saveAdditionalCharge": API_ROUTES.additionalCharges,
  "settings.removeAdditionalCharge": API_ROUTES.additionalCharge,
  "settings.paymentOptions": API_ROUTES.paymentOptions,
  "settings.savePaymentOption": API_ROUTES.paymentOptions,
  "settings.removePaymentOption": API_ROUTES.paymentOption,
  "billingItems.list": API_ROUTES.billingItems,
  "billingItems.create": API_ROUTES.billingItems,
  "billingItems.update": API_ROUTES.billingItem,
  "billingItems.remove": API_ROUTES.billingItem,
  "parties.reconciliations": API_ROUTES.partyReconciliations,
  "parties.reconcile": API_ROUTES.partyReconciliations,
  "accounts.list": API_ROUTES.chartAccounts,
  "accounts.bankAccounts": API_ROUTES.bankAccounts,
  "accounts.bankLedgerAccounts": API_ROUTES.chartAccounts,
  "accounts.createBankAccount": API_ROUTES.bankAccounts,
  "accounts.chart": API_ROUTES.chart,
  "accounts.create": API_ROUTES.chartAccounts,
  "accounts.update": API_ROUTES.chartAccount,
  "accounts.usage": API_ROUTES.chartAccountUsage,
  "accounts.remove": API_ROUTES.chartAccount,
  "accounts.setActive": API_ROUTES.chartAccountActive,
  "accounts.templates": API_ROUTES.chartTemplates,
  "accounts.template": API_ROUTES.chartTemplateDetail,
  "accounts.replacePreview": API_ROUTES.chartTemplatePreview,
  "accounts.applyTemplate": API_ROUTES.chartTemplate,
  "settings.company": API_ROUTES.company,
  "settings.saveCompany": API_ROUTES.company,
  "settings.setup": API_ROUTES.setup,
  "settings.runSetup": API_ROUTES.setup,
  "settings.numbering": API_ROUTES.numbering,
  "settings.saveNumbering": API_ROUTES.numbering,
  "settings.fiscalYears": API_ROUTES.fiscalYears,
  "settings.yearChecklist": API_ROUTES.yearCloseChecks,
  "settings.reviewCheckItem": API_ROUTES.yearCloseItems,
  "settings.markCheckDone": API_ROUTES.yearCloseDone,
  "settings.markCheckNotApplicable": API_ROUTES.yearCloseNotApplicable,
  "settings.reopenCheck": API_ROUTES.yearCloseReopen,
  "settings.yearEndMethod": API_ROUTES.yearEndMethod,
  "settings.saveYearEndMethod": API_ROUTES.yearEndMethod,
  "settings.yearEndPreview": API_ROUTES.yearEnd,
  "settings.closeYear": API_ROUTES.yearEnd,
  "settings.reopenYear": API_ROUTES.yearReopen,
  "settings.setupSteps": API_ROUTES.setupSteps,
  "settings.saveSetupStep": API_ROUTES.setupStep,
  "reporting.frameworks": API_ROUTES.reportFrameworks,
  "reporting.addTag": API_ROUTES.reportTags,
  "reporting.renameTag": API_ROUTES.reportTag,
  "reporting.archiveTag": API_ROUTES.reportTagArchive,
  "reporting.mapping": API_ROUTES.accountMapping,
  "reporting.saveMapping": API_ROUTES.accountMapping,
  "reporting.mappingHistory": API_ROUTES.accountMappingHistory,
  "reporting.templates": API_ROUTES.reportTemplates,
  "reporting.template": API_ROUTES.reportTemplate,
  "reporting.cloneTemplate": API_ROUTES.reportTemplateClone,
  "reporting.saveTemplate": API_ROUTES.reportTemplateById,
  "reporting.deleteTemplate": API_ROUTES.reportTemplateById,
  "reporting.previewTemplate": API_ROUTES.reportTemplatePreview,
  "reporting.statement": API_ROUTES.statement,
  "reporting.finalize": API_ROUTES.statementFinalization,
  "reporting.snapshot": API_ROUTES.statementSnapshot,
  "employees.list": API_ROUTES.employees,
  "employees.record": API_ROUTES.employeeMovements,
  "employees.drafts": API_ROUTES.employeeMovementDrafts,
  "employees.get": API_ROUTES.employeeMovement,
  "employees.post": API_ROUTES.employeeMovementPosting,
  "employees.discard": API_ROUTES.employeeMovement,
  "settings.yearCloseGates": API_ROUTES.yearCloseGates,
  "settings.setYearSoftLock": API_ROUTES.yearSoftLock,
  "settings.withdrawYearClose": API_ROUTES.yearCloseWithdrawal,
  "settings.yearWorkpaper": API_ROUTES.yearWorkpaper,
  "settings.exportYearWorkpaper": API_ROUTES.yearWorkpaperExport,
  "settings.openNextYear": API_ROUTES.nextFiscalYear,
  "settings.hardClosePeriod": API_ROUTES.periodClose,
  "settings.taxCodes": API_ROUTES.taxCodes,
  "settings.withholdingCodes": API_ROUTES.withholdingCodes,
  "settings.countryPack": API_ROUTES.countryPack,
  "settings.emailTemplates": API_ROUTES.emailTemplates,
  "settings.saveEmailTemplate": API_ROUTES.emailTemplate,
  "settings.resetEmailTemplate": API_ROUTES.emailTemplate,
  "documents.requestPdf": API_ROUTES.invoicePdf,
  "documents.pdfStatus": API_ROUTES.documentPdf,
  "documents.emailDraft": API_ROUTES.invoiceEmail,
  "documents.sendEmail": API_ROUTES.invoiceEmail,
  "documents.emails": API_ROUTES.invoiceEmails,
  "settings.branding": API_ROUTES.branding,
  "settings.saveBranding": API_ROUTES.branding,
  "settings.saveBrandingLogo": API_ROUTES.brandingLogo,
  "settings.removeBrandingLogo": API_ROUTES.brandingLogo,
  "settings.saveTaxNames": API_ROUTES.taxNames,
  "settings.renameTaxCode": API_ROUTES.taxCodeName,
  "settings.renameWithholdingCode": API_ROUTES.withholdingCodeName,
  "accounts.rename": API_ROUTES.chartAccountName,

  // Help (ADR-0010). Served from the start: the guides and tickets are the platform's, and a help
  // page built on fixtures would answer questions nobody asked.
  "help.status": API_ROUTES.helpStatus,
  "help.home": API_ROUTES.helpHome,
  "help.category": API_ROUTES.helpCategory,
  "help.article": API_ROUTES.helpArticle,
  "help.search": API_ROUTES.helpSearch,
  "help.recordRead": API_ROUTES.helpReadEvents,
  "help.recordReadDuration": API_ROUTES.helpReadBeacon,
  "support.categories": API_ROUTES.supportCategories,
  "support.tickets": API_ROUTES.supportTickets,
  "support.ticket": API_ROUTES.supportTicket,
  "support.raise": API_ROUTES.supportTickets,
  "support.reply": API_ROUTES.supportTicketMessages,
  "support.stageFiles": API_ROUTES.supportUploads,
  "support.attachmentUrl": API_ROUTES.supportAttachment,
};

/** Members the session endpoints answer, under `/bff` rather than `/api/v1`. */
export const SERVED_BY_BFF: Readonly<Record<string, string>> = {
  "session.switchOrganization": "/bff/organisation",
  "session.openDemo": "/bff/demo",
  "session.signOut": "/bff/logout",
  "session.updateProfile": "/bff/profile",
  "session.changePassword": "/bff/password",
};

export const UNSERVED: Readonly<Record<string, "no route" | ApiRoute>> = {
  "settings.yearEnd": "no route",
  "settings.yearEndAdvance": "no route",
  "settings.yearEndResolve": "no route",
};
